import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { gql } from "@apollo/client";
import { useApolloClient, useQuery } from "@apollo/client/react";
import gqlTag from "graphql-tag";
import { Alert, Button, Group, Skeleton, Stack, Text } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { API_HOSTNAME } from "../../../../server-select";
import { AppBridge } from "./AppBridge";
import { AppApiProxy } from "./AppApiProxy";

const ENTITY_METAFIELDS_QUERY = gql`
  query entityMetafields(
    $entityType: String!
    $entityId: String!
    $filter: EntityMetafieldsFilterInput
  ) {
    entityMetafields(
      entityType: $entityType
      entityId: $entityId
      filter: $filter
    ) {
      id
      namespace
      key
      value
      entityType
      entityId
    }
  }
`;

const ENTITY_METAFIELD_SET_MUTATION = gql`
  mutation entityMetafieldSet($input: SetEntityMetafieldInput!) {
    entityMetafieldSet(input: $input) {
      id
      namespace
      key
      value
    }
  }
`;

const ENTITY_METAFIELD_DELETE_MUTATION = gql`
  mutation entityMetafieldDelete($input: DeleteEntityMetafieldInput!) {
    entityMetafieldDelete(input: $input)
  }
`;

const EXTENSION_TOKEN_QUERY = gql`
  query extensionToken($installationId: UUID!) {
    extensionToken(installationId: $installationId) {
      token
      marketplaceAppId
      appId
    }
  }
`;

/**
 * CDN base URL for resolving extension bundle paths.
 * Must be set via VITE_CDN_BASE_URL environment variable.
 */
const CDN_BASE_URL =
  (import.meta.env.VITE_CDN_BASE_URL as string | undefined)?.replace(
    /\/+$/,
    "",
  ) ?? "";

interface AppIframeHostProps {
  extensionCdnPath?: string;
  appId: string;
  installationId: string;
  grantedPermissions: string[];
  manifestNamespaces: string[];
  extensionPoint: string;
  contextData: Record<string, unknown>;
  maxWidth?: number;
  maxHeight?: number;
}

const HEALTH_CHECK_INTERVAL_MS = 30_000;
const HEALTH_CHECK_TIMEOUT_MS = 5_000;

export function AppIframeHost({
  extensionCdnPath,
  appId,
  installationId,
  grantedPermissions,
  manifestNamespaces,
  extensionPoint,
  contextData,
  maxWidth = 1200,
  maxHeight = 2000,
}: AppIframeHostProps) {
  const src = extensionCdnPath ? `${CDN_BASE_URL}/${extensionCdnPath}` : "";
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const apolloClient = useApolloClient();

  const { data: tokenData } = useQuery<any>(EXTENSION_TOKEN_QUERY, {
    variables: { installationId },
    fetchPolicy: "network-only",
  });

  const moduleToken = tokenData?.extensionToken?.token as string | undefined;
  const marketplaceAppId = tokenData?.extensionToken?.marketplaceAppId as
    | string
    | undefined;
  const moduleTokenRef = useRef<string | undefined>(moduleToken);
  moduleTokenRef.current = moduleToken;

  // Block rendering entirely if extension shares origin with console
  const isSameOrigin = useMemo(() => {
    try {
      return new URL(src).origin === window.location.origin;
    } catch {
      return true;
    }
  }, [src]);

  const [height, setHeight] = useState(400);
  const [healthy, setHealthy] = useState(true);
  const [loading, setLoading] = useState(true);
  const [bridgeInstance, setBridgeInstance] = useState<AppBridge | null>(null);

  const handshakeToken = useMemo(() => crypto.randomUUID(), []);

  const iframeSrc = useMemo(() => {
    const url = new URL(src);
    url.hash = handshakeToken;
    return url.toString();
  }, [src, handshakeToken]);

  const handleReload = useCallback(() => {
    if (iframeRef.current) {
      setLoading(true);
      setHealthy(true);
      iframeRef.current.src = iframeSrc;
    }
  }, [iframeSrc]);

  useEffect(() => {
    // Wait for token before establishing bridge
    if (!moduleToken || !marketplaceAppId) return;

    const expectedOrigin = new URL(src).origin;
    const apiProxy = new AppApiProxy(grantedPermissions);

    const bridge = new AppBridge({
      appId,
      expectedOrigin,
      handshakeToken,
      iframeRef,
      grantedPermissions,
      manifestNamespaces,
      contextData,
      moduleToken,
      apiBaseUrl: API_HOSTNAME,
      marketplaceAppId,
      onApiRequest: async (requestId, document, variables) => {
        const validation = apiProxy.validateAndProxy(document, variables);

        if (!validation.valid) {
          bridge.sendError(
            requestId,
            validation.error ?? "Query validation failed",
          );
          return;
        }

        try {
          const { data, error } = await apolloClient.query<any>({
            query: gqlTag(document),
            variables: variables ? JSON.parse(variables) : undefined,
            fetchPolicy: "network-only",
            context: {
              headers: {
                "x-wp-app-id": appId,
              },
            },
          });

          if (error) {
            bridge.sendError(requestId, error.message);
            return;
          }

          bridge.sendResponse(requestId, data);
        } catch (error) {
          bridge.sendError(
            requestId,
            error instanceof Error ? error.message : "API request failed",
          );
        }
      },
      onNavigate: (path) => {
        navigate(path);
      },
      onToast: (title, message, color) => {
        notifications.show({
          title,
          message,
          color,
          autoClose: 5000,
        });
      },
      onResize: (newHeight) => {
        setHeight(Math.min(newHeight, maxHeight));
      },
      onConfirm: (requestId, title, message) => {
        const confirmed = window.confirm(`${title}\n\n${message}`);
        bridge.sendResponse(requestId, { confirmed });
      },
      onMetafieldGet: async (
        requestId,
        namespace,
        entityType,
        entityId,
        key,
      ) => {
        try {
          const { data } = await apolloClient.query<any>({
            query: ENTITY_METAFIELDS_QUERY,
            variables: {
              entityType,
              entityId,
              filter: { namespace, key },
            },
            fetchPolicy: "network-only",
            context: { headers: { "x-wp-app-id": appId } },
          });
          const metafield = data?.entityMetafields?.[0];
          bridge.sendResponse(requestId, metafield?.value ?? null);
        } catch (error) {
          bridge.sendError(
            requestId,
            error instanceof Error ? error.message : "Failed to get metafield",
          );
        }
      },
      onMetafieldSet: async (
        requestId,
        namespace,
        entityType,
        entityId,
        key,
        value,
      ) => {
        try {
          await apolloClient.mutate({
            mutation: ENTITY_METAFIELD_SET_MUTATION,
            variables: {
              input: { namespace, entityType, entityId, key, value },
            },
            context: { headers: { "x-wp-app-id": appId } },
          });
          bridge.sendResponse(requestId, { success: true });
        } catch (error) {
          bridge.sendError(
            requestId,
            error instanceof Error ? error.message : "Failed to set metafield",
          );
        }
      },
      onSecurityViolation: (type, _details) => {
        notifications.show({
          title: "App Security Violation",
          message: `Extension "${appId}" triggered a security violation: ${type}`,
          color: "red",
          autoClose: 10000,
        });
      },
    });

    bridge.start();
    setBridgeInstance(bridge);

    // Mark loading complete once the iframe fires its load event
    const iframe = iframeRef.current;
    const handleLoad = () => setLoading(false);
    iframe?.addEventListener("load", handleLoad);

    return () => {
      bridge.destroy();
      setBridgeInstance(null);
      iframe?.removeEventListener("load", handleLoad);
    };
  }, [
    src,
    appId,
    handshakeToken,
    grantedPermissions,
    manifestNamespaces,
    contextData,
    maxHeight,
    navigate,
    apolloClient,
    moduleToken,
    marketplaceAppId,
  ]);

  // Health check: ping/pong interval
  useEffect(() => {
    if (!bridgeInstance) return;

    let pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let pendingListener: ((e: MessageEvent) => void) | null = null;

    const intervalId = setInterval(() => {
      let pongReceived = false;

      const handlePong = (event: MessageEvent) => {
        // Only accept pong from the expected iframe source
        if (
          event.data?.type === "pong" &&
          event.source === iframeRef.current?.contentWindow
        ) {
          pongReceived = true;
        }
      };

      pendingListener = handlePong;
      window.addEventListener("message", handlePong);

      bridgeInstance.sendMessage({ type: "ping" });

      pendingTimeoutId = setTimeout(() => {
        window.removeEventListener("message", handlePong);
        pendingListener = null;
        pendingTimeoutId = null;
        if (!pongReceived) {
          setHealthy(false);
        }
      }, HEALTH_CHECK_TIMEOUT_MS);
    }, HEALTH_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      if (pendingTimeoutId) clearTimeout(pendingTimeoutId);
      if (pendingListener)
        window.removeEventListener("message", pendingListener);
    };
  }, [bridgeInstance]);

  // Token refresh: schedule re-fetch ~60s before JWT expiry
  useEffect(() => {
    if (!moduleToken || !bridgeInstance) return;

    const tokenWhenScheduled = moduleToken;

    const decodeExp = (token: string): number | null => {
      try {
        const payload = token.split(".")[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const decoded = JSON.parse(atob(base64));
        return typeof decoded.exp === "number" ? decoded.exp : null;
      } catch {
        return null;
      }
    };

    const exp = decodeExp(moduleToken);
    if (!exp) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = exp - nowSec;

    // Refresh 60s before expiry, or immediately if token expires soon
    const delaySec = secondsUntilExpiry > 70 ? secondsUntilExpiry - 60 : 0;

    const refreshToken = async () => {
      // Skip if token changed since we scheduled this refresh
      if (moduleTokenRef.current !== tokenWhenScheduled) return;

      try {
        const { data } = await apolloClient.query<any>({
          query: EXTENSION_TOKEN_QUERY,
          variables: { installationId },
          fetchPolicy: "network-only",
        });
        const newToken = data?.extensionToken?.token as string | undefined;
        if (newToken && moduleTokenRef.current === tokenWhenScheduled) {
          bridgeInstance.sendMessage({ type: "token.update", token: newToken });
          moduleTokenRef.current = newToken;
        }
      } catch {
        // Token refresh failed — next render cycle will retry
      }
    };

    const timerId = setTimeout(refreshToken, delaySec * 1000);

    return () => clearTimeout(timerId);
  }, [moduleToken, bridgeInstance, apolloClient, installationId]);

  if (isSameOrigin) {
    return (
      <Alert color="red" title="Extension Blocked">
        <Text size="sm">
          This extension cannot be loaded because it shares the same origin as
          the console.
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {!healthy && (
        <Alert color="yellow" title="App Not Responding">
          <Group justify="space-between" align="center">
            <Text size="sm">
              The extension &quot;{appId}&quot; is not responding to health
              checks.
            </Text>
            <Group gap="xs">
              <Button size="xs" variant="light" onClick={handleReload}>
                Reload
              </Button>
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={() => setHealthy(true)}
              >
                Dismiss
              </Button>
            </Group>
          </Group>
        </Alert>
      )}

      {loading && <Skeleton height={height} radius="md" />}

      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title={`App Extension: ${appId} (${extensionPoint})`}
        sandbox="allow-scripts allow-forms allow-same-origin"
        allow="clipboard-write"
        referrerPolicy="no-referrer"
        style={{
          width: "100%",
          height: loading ? 0 : height,
          maxWidth,
          maxHeight,
          border: "none",
          borderRadius: 8,
          overflow: "hidden",
          display: loading ? "none" : "block",
        }}
      />
    </Stack>
  );
}
